import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type { ProductionOrderOperationDTO } from "./floor";

/**
 * Cut jobs (0074, docs/cutting-jobs): the raw stock and pieces of one or more
 * lots cut as one job, as Cut Rite batched them; and the files uploaded to a
 * job or an order.
 */

export type CutJobStatus = "DRAFT" | "CONFIRMED" | "IN_PROGRESS" | "DONE" | "CANCELLED";

export interface CutJobDTO {
  id: number;
  number: number;
  process_id: number | null;
  process_code?: string | null;
  status: CutJobStatus;
  /** Cut Rite's number, as on its paper. Optional. */
  cutrite_number: string | null;
  /** Its earliest lot's due date. */
  due_date?: string | null;
  /** Its place at the station, lower first: set by dragging the job list. */
  position?: number | null;
  note: string | null;
  confirmed_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  created_at?: string | null;
  /** Its lots' numbers, comma separated. */
  lots?: string | null;
  lot_count?: number;
  /** Its virtual parts, comma separated. */
  panels?: string | null;
  sheets_planned?: number | null;
  sheets_used?: number | null;
  pieces_required?: number | null;
  pieces_done?: number | null;
  document_count?: number;
}

/** One lot's virtual part: what a job cuts of it. */
export interface PanelRef {
  production_order_id: number;
  virtual_part_id: number;
}

export interface CutJobLotDTO {
  production_order_id: number;
  number: number;
  status: string;
  part_number: string | null;
  part_name: string | null;
  quantity: number;
  due_date: string | null;
  virtual_parts: string[];
}

export interface CutJobPanelLineDTO {
  part_id: number;
  number: string;
  name: string;
  unit_code: string | null;
  is_consumed_whole: boolean;
  /** From the lots' production BOMs. */
  planned: number;
  used: number;
  scrapped: number;
}

/** One real part a lot plans of a panel type, and what of the job's use it was charged. */
export interface CutJobLotPanelDTO {
  part_id: number;
  number: string;
  planned: number;
  charged: number;
}

/** One lot's share of a panel type in a job. */
export interface CutJobGroupLotDTO {
  production_order_id: number;
  number: number;
  part_number: string | null;
  part_name: string | null;
  quantity: number;
  due_date: string | null;
  panels: CutJobLotPanelDTO[];
}

/** One panel type of a job: its raw stock in total, and lot by lot. */
export interface CutJobPanelGroupDTO {
  virtual_part_id: number;
  virtual_number: string;
  virtual_name: string;
  lines: CutJobPanelLineDTO[];
  lots: CutJobGroupLotDTO[];
}

export interface CutJobDetailDTO {
  job: CutJobDTO;
  lots: CutJobLotDTO[];
  panels: CutJobPanelGroupDTO[];
  /** The pieces' steps of the job's process, reported from the job. */
  pieces: ProductionOrderOperationDTO[];
  /** Step id to the virtual part it is cut from. */
  piece_panels: Record<string, number>;
  documents: ProductionOrderDocumentDTO[];
}

export interface CandidatePanelDTO {
  virtual_part_id: number;
  virtual_number: string;
  virtual_name: string;
  /** The STOCK process it is cut at: CUT (the saw), FOAM (the foam cutter). */
  process_code: string;
  /** "10 × 8862-590 + 2 × 8862-480" */
  planned: string | null;
  sheets: number;
}

export interface CutJobCandidateDTO {
  production_order_id: number;
  number: number;
  kind: string;
  status: string;
  part_number: string | null;
  part_name: string | null;
  quantity: number;
  due_date: string | null;
  panels: CandidatePanelDTO[];
}

export interface OrderCutJobsDTO {
  jobs: CutJobDTO[];
  /** Its raw stock in no job yet. */
  unassigned: CandidatePanelDTO[];
  /** Its own documents and its jobs'. */
  documents: ProductionOrderDocumentDTO[];
}

export interface CutJobUseRequest {
  part_id: number;
  used?: number | null;
  scrapped?: number | null;
  production_scrap_reason_id?: number | null;
  note?: string | null;
  station_id?: number | null;
  operator_badge?: string | null;
}

export class CutJobClient extends ResourceClient<CutJobDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/cut-job");
  }

  /** A draft job with the lots' virtual parts it cuts. */
  createJob(panels: PanelRef[], cutriteNumber: string | null = null, processCode = "CUT"): Promise<CutJobDetailDTO> {
    return this.http.post<CutJobDetailDTO>(`${this.path}/create`, { process_code: processCode, panels, cutrite_number: cutriteNumber });
  }

  /** The station's order: these jobs, first to last. */
  reorder(ids: number[]): Promise<void> {
    return this.http.put<void>(`${this.path}/order`, ids, { responseType: "void" });
  }

  detail(id: number): Promise<CutJobDetailDTO> {
    return this.http.get<CutJobDetailDTO>(`${this.path}/${String(id)}/detail`);
  }

  addPanels(id: number, panels: PanelRef[]): Promise<CutJobDetailDTO> {
    return this.http.post<CutJobDetailDTO>(`${this.path}/${String(id)}/panels`, { panels });
  }

  removePanels(id: number, panels: PanelRef[]): Promise<CutJobDetailDTO> {
    return this.http.post<CutJobDetailDTO>(`${this.path}/${String(id)}/panels/remove`, { panels });
  }

  confirm(id: number): Promise<CutJobDetailDTO> {
    return this.http.post<CutJobDetailDTO>(`${this.path}/${String(id)}/confirm`, {});
  }

  unconfirm(id: number): Promise<CutJobDetailDTO> {
    return this.http.post<CutJobDetailDTO>(`${this.path}/${String(id)}/unconfirm`, {});
  }

  cancel(id: number): Promise<CutJobDetailDTO> {
    return this.http.post<CutJobDetailDTO>(`${this.path}/${String(id)}/cancel`, {});
  }

  finish(id: number, request: { short?: boolean; station_id?: number | null; operator_badge?: string | null } = {}): Promise<CutJobDetailDTO> {
    return this.http.post<CutJobDetailDTO>(`${this.path}/${String(id)}/finish`, request);
  }

  use(id: number, request: CutJobUseRequest): Promise<CutJobDetailDTO> {
    return this.http.post<CutJobDetailDTO>(`${this.path}/${String(id)}/use`, request);
  }

  candidates(processCode = "CUT"): Promise<CutJobCandidateDTO[]> {
    return this.http.get<CutJobCandidateDTO[]>(`${this.path}/candidates`, { query: { process: processCode } });
  }

  forOrder(orderId: number): Promise<OrderCutJobsDTO> {
    return this.http.get<OrderCutJobsDTO>(`${this.path}/order/${String(orderId)}`);
  }

  forStation(stationId: number): Promise<CutJobDTO[]> {
    return this.http.get<CutJobDTO[]>(`${this.path}/station/${String(stationId)}`);
  }
}

/** A finish refused because pieces are not all reported (409 SHORT): the message to ask with, else null. */
export function needsShort(failure: unknown): string | null {
  const body = (failure as { body?: unknown } | null)?.body;
  if (body && typeof body === "object" && (body as { code?: unknown }).code === "SHORT") {
    const message = (body as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }
  return null;
}

export type ProductionDocumentKind = "CUTRITE_CUT" | "CUTRITE_FOAM" | "OTHER";
export const PRODUCTION_DOCUMENT_KINDS: ProductionDocumentKind[] = ["CUTRITE_CUT", "CUTRITE_FOAM", "OTHER"];

export interface ProductionOrderDocumentDTO {
  id: number;
  cut_job_id: number | null;
  production_order_id: number | null;
  kind: ProductionDocumentKind;
  s3_object_id: number;
  file_name: string;
  file_size: number | null;
  note: string | null;
  uploaded_by_account_id: number | null;
  uploaded_by_username?: string | null;
  created_at?: string | null;
}

export class ProductionOrderDocumentClient extends ResourceClient<ProductionOrderDocumentDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/document");
  }

  /** The endpoint reads the raw body: the file goes up as octet-stream, to a job or to an order. */
  upload(owner: { cutJobId?: number; productionOrderId?: number }, file: File, kind: ProductionDocumentKind): Promise<ProductionOrderDocumentDTO> {
    return this.http.request<ProductionOrderDocumentDTO>(`${this.path}/upload`, {
      method: "POST",
      raw: file,
      query: {
        cut_job_id: owner.cutJobId != null ? String(owner.cutJobId) : undefined,
        production_order_id: owner.productionOrderId != null ? String(owner.productionOrderId) : undefined,
        filename: file.name,
        kind,
      },
    });
  }

  /** A link valid a few minutes: PDFs and images open in the browser, other files download. */
  async link(id: number): Promise<string> {
    return (await this.http.get<{ url: string }>(`${this.path}/${String(id)}/link`)).url;
  }
}
